import { useEffect, useState } from "react";
import { apiGet } from "../shared/api.js";
import { copy } from "../shared/copy.js";
import styles from "./BonusesSection.module.css";

interface BonusDto {
  id: string;
  state: "GRANTED" | "RESERVED" | "USED" | "EXPIRED";
  title: string;
  description: string;
  childName: string | null;
  redemptionCode: string | null;
  expiresAt: string;
  usedAt: string | null;
  usedNote: string | null;
}

interface PromoDto {
  id: string;
  title: string;
  description: string | null;
  promoCode: string | null;
  ctaUrl: string | null;
  imageUrl: string | null;
  partner: string;
  validUntil: string | null;
  isSelfBrand: boolean;
}

interface BonusesResp {
  bonuses: BonusDto[];
  promos: PromoDto[];
}

interface Props {
  passportId: string;
  refCode: string;
}

// Renders the "Ваши бонусы" band + the partner "Скидки от друзей" strip.
// Self-brand promos live inside the top group so parents see all
// QuestLegends offers together; partners are visually separated.
export function BonusesSection(props: Props) {
  const [data, setData] = useState<BonusesResp | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await apiGet<BonusesResp>(
          `/api/passport/${encodeURIComponent(props.passportId)}/bonuses?ref=${encodeURIComponent(props.refCode)}`,
        );
        if (!cancelled) setData(r);
      } catch {
        // Bonuses are decoration — a fetch failure just hides the section.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.passportId, props.refCode]);

  if (!data) return null;

  const selfPromos = data.promos.filter((p) => p.isSelfBrand);
  const partnerPromos = data.promos.filter((p) => !p.isSelfBrand);

  if (
    data.bonuses.length === 0 &&
    selfPromos.length === 0 &&
    partnerPromos.length === 0
  ) {
    return null;
  }

  return (
    <div className={styles.wrap}>
      {(data.bonuses.length > 0 || selfPromos.length > 0) && (
        <div className={styles.group}>
          <p className={styles.heading}>{copy.bonuses.heading}</p>
          {data.bonuses.map((b) => (
            <BonusCard key={b.id} bonus={b} />
          ))}
          {selfPromos.map((p) => (
            <PromoCard key={p.id} promo={p} showPartner={false} />
          ))}
        </div>
      )}
      {partnerPromos.length > 0 && (
        <div className={styles.group}>
          <p className={styles.heading}>{copy.bonuses.partnerHeading}</p>
          {partnerPromos.map((p) => (
            <PromoCard key={p.id} promo={p} showPartner />
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function BonusCard({ bonus }: { bonus: BonusDto }) {
  const [copied, setCopied] = useState(false);
  const used = bonus.state === "USED";

  async function copyCode() {
    if (!bonus.redemptionCode) return;
    try {
      await navigator.clipboard.writeText(bonus.redemptionCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent on older browsers.
    }
  }

  return (
    <div className={used ? `${styles.card} ${styles.cardUsed}` : styles.card}>
      <p className={styles.title}>{bonus.title}</p>
      {bonus.childName && (
        <p className={styles.partnerBadge}>
          {copy.bonuses.forChild(bonus.childName)}
        </p>
      )}
      {bonus.description && (
        <p className={styles.description}>{bonus.description}</p>
      )}
      {bonus.redemptionCode && !used && (
        <div className={styles.codeRow}>
          <div>
            <p className={styles.codeLabel}>{copy.bonuses.codeLabel}</p>
            <p className={styles.code}>{bonus.redemptionCode}</p>
          </div>
          <button
            type="button"
            className={styles.copyBtn}
            onClick={copyCode}
          >
            {copied ? copy.bonuses.codeCopied : copy.bonuses.copyCode}
          </button>
        </div>
      )}
      {!bonus.redemptionCode && !used && (
        <p className={styles.meta}>{copy.bonuses.codePending}</p>
      )}
      {used && bonus.usedAt && (
        <p className={styles.meta}>
          {copy.bonuses.usedLabel(formatDate(bonus.usedAt))}
        </p>
      )}
      {!used && (
        <p className={styles.meta}>
          {copy.bonuses.expiresLabel(formatDate(bonus.expiresAt))}
        </p>
      )}
    </div>
  );
}

function PromoCard({
  promo,
  showPartner,
}: {
  promo: PromoDto;
  showPartner: boolean;
}) {
  const [copied, setCopied] = useState(false);
  async function copyCode() {
    if (!promo.promoCode) return;
    try {
      await navigator.clipboard.writeText(promo.promoCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent.
    }
  }
  return (
    <div className={styles.card}>
      {promo.imageUrl && (
        <img className={styles.image} src={promo.imageUrl} alt={promo.title} />
      )}
      {showPartner && <p className={styles.partnerBadge}>{promo.partner}</p>}
      <p className={styles.title}>{promo.title}</p>
      {promo.description && (
        <p className={styles.description}>{promo.description}</p>
      )}
      {promo.promoCode && (
        <div className={styles.codeRow}>
          <div>
            <p className={styles.codeLabel}>{copy.bonuses.codeLabel}</p>
            <p className={styles.code}>{promo.promoCode}</p>
          </div>
          <button type="button" className={styles.copyBtn} onClick={copyCode}>
            {copied ? copy.bonuses.codeCopied : copy.bonuses.copyCode}
          </button>
        </div>
      )}
      {promo.ctaUrl && (
        <a
          className={styles.ctaBtn}
          href={promo.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {copy.bonuses.open}
        </a>
      )}
      {promo.validUntil && (
        <p className={styles.meta}>
          {copy.bonuses.validUntil(formatDate(promo.validUntil))}
        </p>
      )}
    </div>
  );
}
